import { prisma } from '../../shared/prisma';
import { AppError } from '../../shared/errors/AppError';
import { getBulkPresence } from '../../shared/redis';
import bcrypt from 'bcryptjs';
import { Prisma } from '@prisma/client';

export async function listUsers(workspaceId?: string, currentUserId?: string, limit: number = 50, cursor?: string) {
  const where: any = {};
  if (currentUserId) {
    const userRoles = await prisma.userRole.findMany({
      where: { userId: currentUserId },
      include: { role: true },
    });
    const isAdmin = userRoles.some(ur => ur.role.name === 'admin' || ur.role.name === 'super_admin');

    const currentUser = await prisma.user.findUnique({ 
      where: { id: currentUserId },
      include: { department: true, visibleUsers: true }
    });

    if (currentUser?.visibleUsers && currentUser.visibleUsers.length > 0) {
      where.id = { in: [currentUserId, ...currentUser.visibleUsers.map((u: any) => u.id)] };
    } else if (!isAdmin) {
      const isRestricted = userRoles.some(ur => ur.role.name === 'department_restricted');
      
      const commonDeptNames = ['HR', 'IT', 'Human Resources', 'Information Technology', 'Admin'];
      const isCommonDeptUser = currentUser?.department?.name ? commonDeptNames.includes(currentUser.department.name) : false;
      
      if (!isCommonDeptUser) {
        if (currentUser?.companyId) {
          if (isRestricted) {
            where.OR = [
              { id: currentUserId },
              {
                companyId: currentUser.companyId,
                departmentId: currentUser.departmentId,
              },
              {
                department: {
                  name: { in: commonDeptNames }
                }
              },
              { roles: { some: { role: { name: { in: ['admin', 'super_admin'] } } } } }
            ];
          } else {
            where.OR = [
              { id: currentUserId },
              { companyId: currentUser.companyId },
              {
                department: {
                  name: { in: commonDeptNames }
                }
              },
              { roles: { some: { role: { name: { in: ['admin', 'super_admin'] } } } } }
            ];
          }
        } else {
          where.OR = [
            { id: currentUserId },
            {
              department: {
                name: { in: commonDeptNames }
              }
            },
            { roles: { some: { role: { name: { in: ['admin', 'super_admin'] } } } } }
          ];
        }
      }
    }
  }

  const users = await prisma.user.findMany({
    where,
    take: limit,
    ...(cursor ? { skip: 1, cursor: { id: cursor } } : {}),
    select: { 
      id: true, email: true, username: true, displayName: true, avatarUrl: true, presence: true, lastSeen: true, isActive: true, createdAt: true,
      companyId: true, departmentId: true, position: true, employeeId: true,
      company: { select: { id: true, name: true } },
      department: { select: { id: true, name: true } },
      roles: { include: { role: true } }
    },
    orderBy: { displayName: 'asc' },
  });

  const presenceMap = await getBulkPresence(users.map((u: any) => u.id));
  return users.map((u: any) => {
    const { roles: userRoles, ...rest } = u;
    const presetRoles = (userRoles || []).filter((ur: any) => !ur.role.name.startsWith('custom_')).map((ur: any) => ur.role.name);
    const customRoleEntry = (userRoles || []).find((ur: any) => ur.role.name.startsWith('custom_'));
    
    let permissions: string[] = [];
    if (customRoleEntry && Array.isArray(customRoleEntry.role.permissions)) {
      permissions = customRoleEntry.role.permissions;
    } else {
      const PERMISSION_MAP: Record<string, string[]> = {
        super_admin: ['*'], admin: ['*'],
        team_owner: [
          'view_activity', 'view_chat_users', 'chat_send_message', 'chat_receive_message', 'chat_edit_message', 'chat_react_message', 'chat_delete_message', 'chat_pin_message', 'chat_forward_message', 'chat_reply_message', 'chat_file_sharing', 'chat_send_attention', 'chat_text_editing', 'chat_use_emoji',
          'create_teams', 'edit_teams', 'delete_teams', 'manage_team_members', 'team_send_message', 'team_receive_message', 'team_edit_message', 'team_react_message', 'team_delete_message', 'team_pin_message', 'team_forward_message', 'team_reply_message', 'team_file_sharing', 'team_text_editing', 'team_use_emoji',
          'view_users_list'
        ],
        member: [
          'view_activity', 'view_chat_users', 'chat_send_message', 'chat_receive_message', 'chat_edit_message', 'chat_react_message', 'chat_reply_message', 'chat_file_sharing', 'chat_send_attention', 'chat_text_editing', 'chat_use_emoji',
          'team_send_message', 'team_receive_message', 'team_edit_message', 'team_react_message', 'team_reply_message', 'team_file_sharing', 'team_text_editing', 'team_use_emoji',
          'view_users_list'
        ],
        guest: [
          'view_activity', 'chat_receive_message', 'chat_react_message', 'team_receive_message', 'team_react_message'
        ],
        department_restricted: [
          'view_activity', 'view_chat_users', 'chat_send_message', 'chat_receive_message', 'chat_react_message', 'chat_reply_message', 'team_send_message', 'team_receive_message', 'team_react_message', 'team_reply_message'
        ]
      };
      presetRoles.forEach((r: string) => {
        const perms = PERMISSION_MAP[r] || [];
        perms.forEach(p => { if (!permissions.includes(p)) permissions.push(p); });
      });
    }

    return {
      ...rest,
      roles: presetRoles,
      permissions,
      presence: presenceMap[u.id] || 'offline'
    };
  });
}

export async function getUserById(id: string) {
  const user = await prisma.user.findUnique({
    where: { id },
    select: { 
      id: true, email: true, username: true, displayName: true, avatarUrl: true, presence: true, lastSeen: true, createdAt: true, isActive: true, status: true,
      companyId: true, departmentId: true, position: true, employeeId: true,
      company: { select: { id: true, name: true } },
      department: { select: { id: true, name: true } },
      visibleUsers: { select: { id: true } }
    },
  });
  if (!user) throw new AppError(404, 'User not found');
  const presenceMap = await getBulkPresence([user.id]);
  return { ...user, presence: presenceMap[user.id] || 'offline' };
}

export async function getUserByUsername(username: string) {
  const user = await prisma.user.findUnique({
    where: { username },
    select: { 
      id: true, email: true, username: true, displayName: true, avatarUrl: true, presence: true, lastSeen: true, createdAt: true, isActive: true, status: true,
      companyId: true, departmentId: true, position: true, employeeId: true,
      company: { select: { id: true, name: true } },
      department: { select: { id: true, name: true } }
    },
  });
  if (!user) throw new AppError(404, 'User not found');
  const presenceMap = await getBulkPresence([user.id]);
  return { ...user, presence: presenceMap[user.id] || 'offline' };
}

export async function createUser(data: any) {
  const email = data.email || `${data.username}@local.chat`;
  const existingEmail = await prisma.user.findUnique({ where: { email } });
  if (existingEmail) throw new AppError(400, 'Email is already in use');

  const existingUsername = await prisma.user.findUnique({ where: { username: data.username } });
  if (existingUsername) throw new AppError(400, 'Username is already taken');

  const passwordHash = await bcrypt.hash(data.password, 12);

  return prisma.$transaction(async (tx: Prisma.TransactionClient) => {
    const user = await tx.user.create({
      data: {
        email,
        username: data.username,
        displayName: data.displayName,
        passwordHash,
        avatarUrl: data.avatarUrl,
        companyId: data.companyId || null,
        departmentId: data.departmentId || null,
        position: data.position || null,
        employeeId: data.employeeId || null,
      },
    });

    if (data.roles && Array.isArray(data.roles)) {
      for (const roleName of data.roles) {
        let roleRecord = await tx.role.findUnique({ where: { name: roleName } });
        if (!roleRecord) {
          roleRecord = await tx.role.create({ data: { name: roleName } });
        }
        await tx.userRole.create({
          data: {
            userId: user.id,
            roleId: roleRecord.id,
          },
        });
      }
    } else if (data.role) {
      // Fallback for older API calls
      let roleRecord = await tx.role.findUnique({ where: { name: data.role } });
      if (!roleRecord) {
        roleRecord = await tx.role.create({ data: { name: data.role } });
      }
      await tx.userRole.create({
        data: {
          userId: user.id,
          roleId: roleRecord.id,
        },
      });
    }

    // Handle custom granular permissions — stored in a per-user custom role
    if (data.permissions && Array.isArray(data.permissions) && data.permissions.length > 0) {
      const customRoleName = `custom_${user.id}`;
      const existing = await tx.role.findUnique({ where: { name: customRoleName } });
      let customRole;
      if (existing) {
        customRole = await tx.role.update({
          where: { name: customRoleName },
          data: { permissions: data.permissions },
        });
      } else {
        customRole = await tx.role.create({
          data: { name: customRoleName, permissions: data.permissions },
        });
      }
      // Only create UserRole link if not already linked
      const existingLink = await tx.userRole.findFirst({ where: { userId: user.id, roleId: customRole.id } });
      if (!existingLink) {
        await tx.userRole.create({ data: { userId: user.id, roleId: customRole.id } });
      }
    }

    const { passwordHash: _, ...userWithoutPassword } = user;
    return userWithoutPassword;
  });
}

export async function updateUser(id: string, data: any) {
  const user = await prisma.user.findUnique({ where: { id } });
  if (!user) throw new AppError(404, 'User not found');

  const updateData: any = {
    displayName: data.displayName,
    avatarUrl: data.avatarUrl,
    status: data.status,
    isActive: data.isActive,
    email: data.email === '' ? `${data.username || user.username}@local.chat` : data.email,
    username: data.username,
    companyId: data.companyId !== undefined ? data.companyId : undefined,
    departmentId: data.departmentId !== undefined ? data.departmentId : undefined,
    position: data.position !== undefined ? data.position : undefined,
    employeeId: data.employeeId !== undefined ? data.employeeId : undefined,
  };

  if (data.password) {
    updateData.passwordHash = await bcrypt.hash(data.password, 12);
  }

  return prisma.$transaction(async (tx) => {
    const updatedUser = await tx.user.update({
      where: { id },
      data: updateData,
      select: { 
        id: true, email: true, username: true, displayName: true, avatarUrl: true, isActive: true, status: true,
        companyId: true, departmentId: true, position: true, employeeId: true,
        company: { select: { id: true, name: true } },
        department: { select: { id: true, name: true } }
      },
    });

    if (data.roles && Array.isArray(data.roles)) {
      // Delete only non-custom role assignments before re-assigning
      const existingUserRoles = await tx.userRole.findMany({
        where: { userId: id },
        include: { role: true },
      });
      for (const ur of existingUserRoles) {
        if (!ur.role.name.startsWith('custom_')) {
          await tx.userRole.delete({ where: { id: ur.id } });
        }
      }
      for (const roleName of data.roles) {
        let roleRecord = await tx.role.findUnique({ where: { name: roleName } });
        if (!roleRecord) {
          roleRecord = await tx.role.create({ data: { name: roleName } });
        }
        await tx.userRole.create({
          data: {
            userId: id,
            roleId: roleRecord.id,
          },
        });
      }
    } else if (data.role) {
      await tx.userRole.deleteMany({ where: { userId: id } });
      let roleRecord = await tx.role.findUnique({ where: { name: data.role } });
      if (!roleRecord) {
        roleRecord = await tx.role.create({ data: { name: data.role } });
      }
      await tx.userRole.create({
        data: {
          userId: id,
          roleId: roleRecord.id,
        },
      });
    }

    // Upsert the custom_<userId> role with granular permissions
    if (data.permissions && Array.isArray(data.permissions)) {
      const customRoleName = `custom_${id}`;
      const existingCustomRole = await tx.role.findUnique({ where: { name: customRoleName } });
      let customRole;
      if (existingCustomRole) {
        customRole = await tx.role.update({
          where: { name: customRoleName },
          data: { permissions: data.permissions },
        });
      } else if (data.permissions.length > 0) {
        customRole = await tx.role.create({
          data: { name: customRoleName, permissions: data.permissions },
        });
      }
      if (customRole) {
        const existingLink = await tx.userRole.findFirst({ where: { userId: id, roleId: customRole.id } });
        if (!existingLink) {
          await tx.userRole.create({ data: { userId: id, roleId: customRole.id } });
        }
      }
    }

    return updatedUser;
  });
}

export async function updateProfile(userId: string, data: { displayName?: string; avatarUrl?: string | null; status?: string | null; workLocation?: string | null }) {
  return prisma.user.update({
    where: { id: userId },
    data,
    select: { id: true, email: true, username: true, displayName: true, avatarUrl: true, status: true, workLocation: true },
  });
}

export async function activateUser(userId: string) {
  return prisma.user.update({ where: { id: userId }, data: { isActive: true } });
}

export async function deactivateUser(userId: string) {
  return prisma.user.update({ where: { id: userId }, data: { isActive: false } });
}

export async function deleteUser(userId: string) {
  return prisma.user.delete({ where: { id: userId } });
}

export async function searchUsers(query: string, limit: number = 20, currentUserId?: string) {
  const where: any = {
    OR: [
      { displayName: { contains: query, mode: 'insensitive' } },
      { username: { contains: query, mode: 'insensitive' } },
      { email: { contains: query, mode: 'insensitive' } },
    ],
  };

  if (currentUserId) {
    const userRoles = await prisma.userRole.findMany({
      where: { userId: currentUserId },
      include: { role: true },
    });
    const isAdmin = userRoles.some(ur => ur.role.name === 'admin' || ur.role.name === 'super_admin');
    const isRestricted = userRoles.some(ur => ur.role.name === 'department_restricted');

    const currentUser = await prisma.user.findUnique({ 
      where: { id: currentUserId },
      include: { department: true, visibleUsers: true }
    });

    if (currentUser?.visibleUsers && currentUser.visibleUsers.length > 0) {
      where.AND = [
        { id: { in: [currentUserId, ...currentUser.visibleUsers.map((u: any) => u.id)] } }
      ];
    } else if (!isAdmin) {
      const commonDeptNames = ['HR', 'IT', 'Human Resources', 'Information Technology', 'Admin'];
      const isCommonDeptUser = currentUser?.department?.name ? commonDeptNames.includes(currentUser.department.name) : false;
      
      if (!isCommonDeptUser) {
        if (currentUser?.companyId) {
          if (isRestricted) {
            where.AND = [
              {
                OR: [
                  { id: currentUserId },
                  {
                    companyId: currentUser.companyId,
                    departmentId: currentUser.departmentId,
                  },
                  {
                    department: {
                      name: { in: commonDeptNames }
                    }
                  },
                  { roles: { some: { role: { name: { in: ['admin', 'super_admin'] } } } } }
                ]
              }
            ];
          } else {
            where.AND = [
              {
                OR: [
                  { id: currentUserId },
                  { companyId: currentUser.companyId },
                  {
                    department: {
                      name: { in: commonDeptNames }
                    }
                  },
                  { roles: { some: { role: { name: { in: ['admin', 'super_admin'] } } } } }
                ]
              }
            ];
          }
        } else {
          where.AND = [
            {
              OR: [
                { id: currentUserId },
                {
                  department: {
                    name: { in: commonDeptNames }
                  }
                },
                { roles: { some: { role: { name: { in: ['admin', 'super_admin'] } } } } }
              ]
            }
          ];
        }
      }
    }
  }

  const users = await prisma.user.findMany({
    where,
    select: { 
      id: true, username: true, displayName: true, avatarUrl: true, presence: true, isActive: true, email: true, createdAt: true,
      companyId: true, departmentId: true, position: true, employeeId: true,
      company: { select: { id: true, name: true } },
      department: { select: { id: true, name: true } },
      roles: { include: { role: true } }
    },
    take: limit,
  });

  const presenceMap = await getBulkPresence(users.map((u: any) => u.id));
  return users.map((u: any) => {
    const { roles: userRoles, ...rest } = u;
    const presetRoles = (userRoles || []).filter((ur: any) => !ur.role.name.startsWith('custom_')).map((ur: any) => ur.role.name);
    const customRoleEntry = (userRoles || []).find((ur: any) => ur.role.name.startsWith('custom_'));
    const permissions = customRoleEntry && Array.isArray(customRoleEntry.role.permissions) ? customRoleEntry.role.permissions : [];
    return {
      ...rest,
      roles: presetRoles,
      permissions,
      presence: presenceMap[u.id] || 'offline'
    };
  });
}

export async function getUserPresenceBulk(userIds: string[]) {
  return getBulkPresence(userIds);
}

export async function assignVisibility(userId: string, visibleUserIds: string[]) {
  return prisma.user.update({
    where: { id: userId },
    data: {
      visibleUsers: {
        set: visibleUserIds.map(id => ({ id }))
      }
    }
  });
}
